<template>
  <v-row>
    <v-breadcrumbs :items="['admin', 'dividend backfill']" divider="/"></v-breadcrumbs>
  </v-row>

  <!-- input card -->
  <v-row>
    <v-col cols="12" md="8">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">dividend backfill</v-card-title>
          <v-card-subtitle class="text-left">fetches historical dividend and distribution data from Yahoo Finance for all securities in the portfolio. review and edit individual rows before saving. manual entries (protected) are never overwritten.</v-card-subtitle>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col>
              <v-text-field
                v-model="startDate"
                type="date"
                label="start date"
                density="compact"
                variant="outlined"
                hide-details
                @update:modelValue="reset"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                v-model="endDate"
                type="date"
                label="end date"
                density="compact"
                variant="outlined"
                hide-details
                @update:modelValue="reset"
              ></v-text-field>
            </v-col>
            <v-col cols="auto" class="text-grey text-caption">or</v-col>
            <v-col>
              <v-select
                v-model="selectedFY"
                :items="financialYears"
                label="FY shortcut"
                density="compact"
                variant="outlined"
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="auto">
              <v-btn
                @click="fetchPreview"
                :loading="fetchLoading"
                :disabled="!startDate || !endDate"
                color="primary"
              >fetch dividends</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <template v-if="result">
    <!-- summary chips -->
    <v-row class="mt-1">
      <v-col cols="auto">
        <v-chip v-if="editableRows.length > 0" color="green" variant="tonal">{{ result.summary.new }} new</v-chip>
      </v-col>
      <v-chip v-if="result.summary.update > 0" color="orange" variant="tonal" class="ml-2">{{ result.summary.update }} updates</v-chip>
      <v-chip v-if="result.summary.protected > 0" color="grey" variant="tonal" class="ml-2">{{ result.summary.protected }} protected</v-chip>
      <v-chip v-if="result.summary.no_data > 0" color="grey" variant="tonal" class="ml-2">{{ result.summary.no_data }} tickers with no data</v-chip>
      <v-chip v-if="result.summary.failed > 0" color="red" variant="tonal" class="ml-2">{{ result.summary.failed }} failed</v-chip>
    </v-row>

    <!-- results table -->
    <v-row>
      <v-col cols="12">
        <v-card :elevation="1">
          <v-card-item>
            <v-card-title class="text-left">results</v-card-title>
            <v-card-subtitle class="text-left">edit amounts or remove rows before saving. protected rows (manual entries) are shown for context only and will not be modified.</v-card-subtitle>
          </v-card-item>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-table density="compact">
              <thead>
                <tr>
                  <th>ticker</th>
                  <th>ex date</th>
                  <th>payment date</th>
                  <th class="text-right" style="width:150px">amount / unit</th>
                  <th>currency</th>
                  <th class="text-right" style="width:150px">franking cr / unit</th>
                  <th>status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in allDisplayRows"
                  :key="row._key"
                  :class="row.status === 'protected' ? 'text-grey' : ''"
                >
                  <td class="font-weight-medium">{{ row.ticker }}</td>
                  <td>{{ row.ex_date }}</td>
                  <td>
                    <v-text-field
                      v-if="row.status !== 'protected'"
                      v-model="editableRows[row._editIdx].payment_date"
                      density="compact"
                      variant="plain"
                      hide-details
                      type="date"
                      style="min-width:130px"
                    ></v-text-field>
                    <span v-else class="text-caption">—</span>
                  </td>
                  <td class="text-right">
                    <v-text-field
                      v-if="row.status !== 'protected'"
                      v-model.number="editableRows[row._editIdx].amount_per_unit"
                      density="compact"
                      variant="outlined"
                      hide-details
                      type="number"
                      style="min-width:120px"
                    ></v-text-field>
                    <span v-else>{{ row.amount_per_unit }}</span>
                  </td>
                  <td>{{ row.currency }}</td>
                  <td class="text-right">
                    <v-text-field
                      v-if="row.status !== 'protected'"
                      v-model.number="editableRows[row._editIdx].franking_credits_per_unit"
                      density="compact"
                      variant="outlined"
                      hide-details
                      type="number"
                      style="min-width:120px"
                      placeholder="—"
                    ></v-text-field>
                    <span v-else class="text-caption">{{ row.franking_credits_per_unit ?? '—' }}</span>
                  </td>
                  <td>
                    <v-chip size="x-small" :color="statusColor(row.status)" variant="tonal">{{ row.status }}</v-chip>
                  </td>
                  <td>
                    <v-btn
                      v-if="row.status !== 'protected'"
                      icon="mdi-delete-outline"
                      size="x-small"
                      variant="text"
                      color="grey"
                      @click="removeRow(row._editIdx)"
                    ></v-btn>
                  </td>
                </tr>
                <tr v-if="allDisplayRows.length === 0">
                  <td colspan="8" class="text-grey text-caption pa-4 text-center">no dividend data found for this date range</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
          <v-divider v-if="editableRows.length > 0"></v-divider>
          <v-card-text v-if="editableRows.length > 0" class="pa-3 text-right">
            <v-btn
              @click="save"
              :loading="saveLoading"
              color="primary"
            >save {{ editableRows.length }} records</v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </template>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';

export default {
  data() {
    return {
      selectedFY: null,
      startDate: '',
      endDate: '',
      fetchLoading: false,
      saveLoading: false,
      result: null,
      editableRows: [],
      protectedRows: [],
    };
  },
  computed: {
    ...mapState({ token: 'token' }),
    financialYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const latestEndYear = currentMonth >= 6 ? currentYear : currentYear - 1;
      const years = [];
      for (let endYear = latestEndYear; endYear >= latestEndYear - 5; endYear--) {
        years.push('FY' + (endYear - 1).toString().slice(2) + '/' + endYear.toString().slice(2));
      }
      return years;
    },
    allDisplayRows() {
      const editable = this.editableRows.map((row, idx) => ({ ...row, _editIdx: idx, _key: `e-${idx}` }));
      const protected_ = this.protectedRows.map((row, idx) => ({ ...row, _editIdx: null, _key: `p-${idx}` }));
      return [...editable, ...protected_].sort((a, b) => a.ticker.localeCompare(b.ticker) || a.ex_date.localeCompare(b.ex_date));
    },
  },
  watch: {
    selectedFY(val) {
      if (!val) return;
      const parts = val.replace('FY', '').split('/');
      const endYear = 2000 + parseInt(parts[1]);
      this.endDate = endYear + '-06-30';
      this.startDate = (endYear - 1) + '-07-01';
      this.reset();
    }
  },
  methods: {
    ...mapMutations(['showSnackbar']),
    reset() {
      this.result = null;
      this.editableRows = [];
      this.protectedRows = [];
    },
    async fetchPreview() {
      this.fetchLoading = true;
      this.reset();
      try {
        const response = await axios.post(
          '/securities/dividends/backfill/preview',
          { start_date: this.startDate, end_date: this.endDate },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.result = response.data;
        this.editableRows = response.data.rows.filter(r => r.status !== 'protected');
        this.protectedRows = response.data.rows.filter(r => r.status === 'protected');
      } catch (error) {
        this.showSnackbar({ message: 'Fetch failed: ' + (error.response?.data?.error || error.message) });
      }
      this.fetchLoading = false;
    },
    removeRow(idx) {
      this.editableRows.splice(idx, 1);
    },
    async save() {
      this.saveLoading = true;
      try {
        const response = await axios.post(
          '/securities/dividends/backfill/save',
          { rows: this.editableRows },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.showSnackbar({ message: `${response.data.saved} dividend records saved` });
        this.editableRows = [];
        this.result.summary.new = 0;
        this.result.summary.update = 0;
      } catch (error) {
        this.showSnackbar({ message: 'Save failed: ' + (error.response?.data?.error || error.message) });
      }
      this.saveLoading = false;
    },
    statusColor(status) {
      if (status === 'new') return 'green';
      if (status === 'update') return 'orange';
      return 'grey';
    },
  }
};
</script>
